import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollTracking, useTimeTracking } from '@/hooks/useScrollTracking';
import { hasAnalyticsConsent } from '@/utils/cookieConsent';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-071CK5ETSY';

// Debug mode for development
const DEBUG_MODE = process.env.NODE_ENV === 'development';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    gaLoaded?: boolean;
  }
}

const GoogleAnalytics = () => {
  const location = useLocation();
  const scriptLoadedRef = useRef(false);

  // Add scroll and time tracking
  useScrollTracking();
  useTimeTracking();

  useEffect(() => {
    // Only load GA if user has consented to analytics
    if (!hasAnalyticsConsent()) {
      if (DEBUG_MODE) console.log('GA: No analytics consent, skipping script load');
      return;
    }

    // Prevent duplicate script loading
    if (scriptLoadedRef.current || window.gaLoaded) {
      if (DEBUG_MODE) console.log('GA: Script already loaded, skipping');
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`);
    if (existingScript) {
      if (DEBUG_MODE) console.log('GA: Script element already exists');
      scriptLoadedRef.current = true;
      window.gaLoaded = true;
      return;
    }

    if (DEBUG_MODE) console.log('GA: Loading script for', GA_MEASUREMENT_ID);

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    script.onload = () => {
      if (DEBUG_MODE) console.log('GA: Script loaded successfully');
      scriptLoadedRef.current = true;
      window.gaLoaded = true;
    };

    script.onerror = () => {
      console.error('GA: Failed to load script');
    };

    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
      if (DEBUG_MODE) console.log('GA: gtag called with', arguments);
    };
    
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      debug_mode: DEBUG_MODE,
      send_page_view: true
    });

    if (DEBUG_MODE) {
      console.log('GA: Configured with ID', GA_MEASUREMENT_ID);
      console.log('GA: Initial page view sent for', window.location.href);
    }

    return () => {
      // Don't remove script on cleanup to prevent re-loading
      if (DEBUG_MODE) console.log('GA: Component unmounting, keeping script');
    };
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!hasAnalyticsConsent()) {
      if (DEBUG_MODE) console.log('GA: No analytics consent, skipping page view');
      return;
    }

    if (window.gtag && scriptLoadedRef.current) {
      const pageView = {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href
      };

      window.gtag('config', GA_MEASUREMENT_ID, pageView);
      
      // Also send as event for better tracking
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });

      if (DEBUG_MODE) {
        console.log('GA: Page view tracked', pageView);
      }
    } else if (DEBUG_MODE) {
      console.log('GA: Skipping page view - gtag not ready');
    }
  }, [location]);

  return null;
};

// Custom tracking functions for events with V2.0 parameters
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GA: No analytics consent, skipping event', eventName);
    return;
  }
  
  if (window.gtag) {
    window.gtag('event', eventName, {
      event_category: 'engagement',
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      ...parameters,
    });
    if (DEBUG_MODE) console.log('GA: Event tracked', eventName, parameters);
  }
};

export const trackConversion = (eventName: string, value?: number) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('GA: No analytics consent, skipping conversion', eventName);
    return;
  }
  
  if (window.gtag) {
    window.gtag('event', eventName, {
      event_category: 'conversion',
      event_label: eventName,
      app_version: '2.0',
      layout_version: 'interactive_preview',
      value: value,
    });
    if (DEBUG_MODE) console.log('GA: Conversion tracked', eventName, value);
  }
};

export const trackSubscription = (email: string, plan?: string) => {
  if (window.gtag) {
    window.gtag('event', 'newsletter_signup', {
      event_category: 'conversion',
      event_label: 'newsletter_subscription',
      user_email: email,
      subscription_plan: plan || 'trial',
      app_version: '2.0',
      layout_version: 'interactive_preview',
    });
    if (DEBUG_MODE) console.log('GA: Subscription tracked', email, plan);
  }
};

// New V2.0 specific tracking functions
export const trackInteractivePreview = (action: string, templateId?: string) => {
  if (window.gtag) {
    window.gtag('event', 'interactive_preview_engagement', {
      event_category: 'preview_interaction',
      action: action,
      template_id: templateId,
      app_version: '2.0',
      engagement_type: 'preview_interaction',
    });
    if (DEBUG_MODE) console.log('GA: Interactive preview tracked', action, templateId);
  }
};

export const trackConversionFunnel = (step: string, stepNumber: number) => {
  if (window.gtag) {
    window.gtag('event', 'conversion_funnel', {
      event_category: 'funnel',
      funnel_step: step,
      step_number: stepNumber,
      app_version: '2.0',
      funnel_type: 'newsletter_signup_v2',
    });
    if (DEBUG_MODE) console.log('GA: Funnel step tracked', step, stepNumber);
  }
};

export const trackFormEngagement = (formType: string, engagement: string) => {
  if (window.gtag) {
    window.gtag('event', 'form_engagement', {
      event_category: 'form_interaction',
      form_type: formType,
      engagement_type: engagement,
      app_version: '2.0',
    });
    if (DEBUG_MODE) console.log('GA: Form engagement tracked', formType, engagement);
  }
};

export default GoogleAnalytics;